import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const ListDrawerContext = createContext(null);

export const useListDrawerContext = () => {
  const context = useContext(ListDrawerContext);
  if (!context) {
    throw new Error('useListDrawerContext must be used within a TableProvider');
  }
  return context;
};

export const ListDrawerProvider = ({
  children,
  actionsCallbacks = [],
  groups = undefined,
  items = undefined,
  loadGroupApi = undefined,
  resetGroup = undefined,
  editGroupCallback = undefined,
  deleteGroupCallback = undefined,
  onUpdate = undefined,
  selectedValues = ['id'],
  selectedKey = 'id',
  onSelect = undefined,
  isLoading = false,
  defaultSelected = null,
  hasToolbar = true,
  renderListItem = undefined,
  allowResize = false,
  minSizeFirstPane = 0,
  maxSizeFirstPane = 50,
  initialFirstPaneSize = 25,
  styleConfig = undefined,
}) => {
  const [actions] = useState(actionsCallbacks); // { name: "", callback: () => void }
  const [selected, setSelected] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState([]);
  const [search, setSearch] = useState('');
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    if (defaultSelected && defaultSelected !== selected) {
      setSelected(defaultSelected);
    }
  }, [defaultSelected]);

  useEffect(() => {
    if (onSelect) onSelect(selected);
  }, [selected]);

  useEffect(() => {
    update({ search: search ? search : undefined });
  }, [search]);

  const update = useCallback(
    ({ search = undefined }) => {
      if (onUpdate) {
        onUpdate(expandedGroups, { search });
      }
    },
    [expandedGroups],
  );

  const value = useMemo(
    () => ({
      actions,
      groups,
      items,
      selected,
      setSelected,
      expandedGroups,
      setExpandedGroups,
      loadGroupApi,
      resetGroup,
      editGroupCallback,
      deleteGroupCallback,
      selectedKey,
      selectedValues,
      search,
      update,
      setSearch,
      disabled,
      setDisabled,
      isLoading,
      hasToolbar,
      renderListItem,
      allowResize,
      initialFirstPaneSize,
      styleConfig,
      minSizeFirstPane,
      maxSizeFirstPane,
    }),
    [groups, items, selected, expandedGroups, disabled, isLoading, renderListItem],
  );

  return <ListDrawerContext.Provider value={value}>{children}</ListDrawerContext.Provider>;
};
